import { useMutation, useQueryClient } from "@tanstack/react-query";

import { wishlistApi } from "@/features/engagement/services/wishlist.api";

interface ToggleWishlistInput {
  productId: string;
  isFavorite: boolean;
}

/**
 * One mutation covers both directions — the caller already knows the
 * product's current favorite state (it's rendering the heart icon), so it
 * passes `isFavorite` rather than this hook re-deriving it from a second
 * cache read. Invalidates `["wishlist"]` (membership changed) and
 * `["products"]` (the toggled product's `favoritesCount` changed too, per
 * server/src/services/wishlist.service.ts).
 */
export function useToggleWishlist() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ productId, isFavorite }: ToggleWishlistInput) =>
      isFavorite
        ? wishlistApi.removeFromWishlist(productId)
        : wishlistApi.addToWishlist(productId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["wishlist"] });
      void queryClient.invalidateQueries({ queryKey: ["products"] });
    },
  });
}
